import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Space,
  Table,
  Tag,
  Upload,
  message,
} from 'antd';
import { CloudUploadOutlined, InboxOutlined } from '@ant-design/icons';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import tracxnUploadService from '../../services/tracxnUploadService';

const { Dragger } = Upload;

const UPLOAD_SECTIONS = [
  {
    field: 'companies',
    title: 'Companies',
    description: 'Company profile dump (.json or .jsonl)',
  },
  {
    field: 'financials',
    title: 'Financials',
    description: 'Financial statements dump (.json or .jsonl)',
  },
  {
    field: 'captables',
    title: 'Cap tables',
    description: 'Cap table dump (.json or .jsonl)',
  },
  {
    field: 'competitors',
    title: 'Competitors',
    description: 'Competitors dump (.json or .jsonl)',
  },
  {
    field: 'fundingRounds',
    title: 'Funding rounds',
    description: 'Funding / transactions dump (.json or .jsonl)',
  },
];

const SECTION_LABELS = Object.fromEntries(
  UPLOAD_SECTIONS.map(({ field, title }) => [field, title])
);

const emptyFilesState = () =>
  Object.fromEntries(UPLOAD_SECTIONS.map(({ field }) => [field, []]));

function SectionUpload({ field, title, description, fileList, onChange, disabled }) {
  const handleChange = ({ fileList: nextList }) => {
    const files = nextList
      .map((item) => item.originFileObj)
      .filter(Boolean)
      .slice(0, 10);
    onChange(field, files);
  };

  const uploadList = fileList.map((file, index) => ({
    uid: `${field}-${index}-${file.name}`,
    name: file.name,
    status: 'done',
    originFileObj: file,
  }));

  return (
    <Card size="small" title={title} className="h-full">
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <Dragger
        accept=".json,.jsonl,application/json"
        multiple
        fileList={uploadList}
        beforeUpload={() => false}
        onChange={handleChange}
        disabled={disabled}
        maxCount={10}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag files here</p>
        <p className="ant-upload-hint">Up to 10 files per section. JSON or JSONL only.</p>
      </Dragger>
    </Card>
  );
}

function formatSectionStats(sectionKey, stats) {
  if (!stats) return null;
  if (stats.skipped) {
    return { section: SECTION_LABELS[sectionKey] || sectionKey, status: 'Skipped' };
  }
  return {
    section: SECTION_LABELS[sectionKey] || sectionKey,
    domainsWritten: stats.domainsWritten ?? 0,
    domainsInDump: stats.domainsInDump ?? 0,
    rowsInDump: stats.rowsInDump ?? 0,
    rowsSkippedNotListed: stats.rowsSkippedNotListed ?? 0,
    parseErrors: stats.parseErrors?.length ?? 0,
  };
}

export default function TracxnUploadPage() {
  const { hasAnyPermission, isSuperAdmin } = useAuth();
  const canUpload =
    isSuperAdmin ||
    hasAnyPermission([PERMISSIONS.PRODUCTS_EDIT, PERMISSIONS.PRODUCTS_CREATE]);

  const [filesByField, setFilesByField] = useState(emptyFilesState);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const totalFiles = useMemo(
    () => Object.values(filesByField).reduce((sum, files) => sum + files.length, 0),
    [filesByField]
  );

  const handleFilesChange = useCallback((field, files) => {
    setFilesByField((prev) => ({ ...prev, [field]: files }));
    setResult(null);
  }, []);

  const handleClear = () => {
    setFilesByField(emptyFilesState());
    setResult(null);
  };

  const handleSubmit = async () => {
    if (!canUpload) {
      message.error('You do not have permission to upload Tracxn data.');
      return;
    }
    if (totalFiles === 0) {
      message.warning('Select at least one file to upload.');
      return;
    }

    setUploading(true);
    setResult(null);
    try {
      const payload = await tracxnUploadService.uploadTracxnFiles(filesByField);
      setResult(payload);
      message.success(payload.message || 'Tracxn data ingested successfully.');
      setFilesByField(emptyFilesState());
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Tracxn upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const sectionRows = useMemo(() => {
    if (!result?.summary?.sections) return [];
    return Object.entries(result.summary.sections)
      .map(([key, stats]) => formatSectionStats(key, stats))
      .filter(Boolean);
  }, [result]);

  const sectionColumns = [
    { title: 'Section', dataIndex: 'section', key: 'section' },
    {
      title: 'Status',
      key: 'status',
      render: (_, row) =>
        row.status ? (
          <Tag>{row.status}</Tag>
        ) : (
          <Tag color="green">Ingested</Tag>
        ),
    },
    { title: 'Domains written', dataIndex: 'domainsWritten', key: 'domainsWritten' },
    { title: 'Domains in dump', dataIndex: 'domainsInDump', key: 'domainsInDump' },
    { title: 'Rows in dump', dataIndex: 'rowsInDump', key: 'rowsInDump' },
    {
      title: 'Skipped (not listed)',
      dataIndex: 'rowsSkippedNotListed',
      key: 'rowsSkippedNotListed',
    },
    { title: 'Parse errors', dataIndex: 'parseErrors', key: 'parseErrors' },
  ];

  return (
    <DashboardPageWrapper>
      <div className="p-6 space-y-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Tracxn data upload</h1>
            <p className="text-sm text-gray-500 mt-1 max-w-3xl">
              Upload Tracxn JSON or JSONL dumps for company intelligence. Only ACTIVE Pre-IPO
              products with a configured domain are ingested.
            </p>
          </div>
          <Space wrap>
            <Button onClick={handleClear} disabled={uploading || totalFiles === 0}>
              Clear files
            </Button>
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              loading={uploading}
              disabled={!canUpload || totalFiles === 0}
              onClick={handleSubmit}
            >
              Upload &amp; ingest ({totalFiles})
            </Button>
          </Space>
        </div>

        {!canUpload && (
          <Alert
            type="warning"
            showIcon
            message="You need Products Edit or Products Create permission to upload Tracxn files."
          />
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {UPLOAD_SECTIONS.map((section) => (
            <SectionUpload
              key={section.field}
              {...section}
              fileList={filesByField[section.field]}
              onChange={handleFilesChange}
              disabled={uploading || !canUpload}
            />
          ))}
        </div>

        {result?.summary && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
            <Alert type="success" showIcon message={result.message} />

            <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
              <Descriptions.Item label="Batch ID">
                {result.summary.ingestBatchId || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Listed products">
                {result.summary.listedProductCount ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Domains ingested">
                {result.summary.domainsIngested?.length ?? 0}
              </Descriptions.Item>
            </Descriptions>

            {sectionRows.length > 0 && (
              <Table
                size="small"
                rowKey="section"
                pagination={false}
                columns={sectionColumns}
                dataSource={sectionRows}
              />
            )}

            {result.summary.productsMissingDomain?.length > 0 && (
              <Collapse
                items={[
                  {
                    key: 'missing-domain',
                    label: `Products missing domain (${result.summary.productsMissingDomain.length})`,
                    children: (
                      <Table
                        size="small"
                        rowKey="productId"
                        pagination={{ pageSize: 10 }}
                        dataSource={result.summary.productsMissingDomain}
                        columns={[
                          { title: 'Product', dataIndex: 'name', key: 'name' },
                          { title: 'Slug', dataIndex: 'slug', key: 'slug' },
                        ]}
                      />
                    ),
                  },
                ]}
              />
            )}

            {result.summary.domainsIngested?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Domains ingested</p>
                <Space size={[4, 4]} wrap>
                  {result.summary.domainsIngested.map((domain) => (
                    <Tag key={domain}>{domain}</Tag>
                  ))}
                </Space>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardPageWrapper>
  );
}
